import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import cond from 'lodash/cond';
import * as Collapsible from '@radix-ui/react-collapsible';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { SuiCheckbox } from '@splashthat/sui';
import CaretDown from '@splashthat/doppler/icons/CaretDown';
import {
    getFormSessionStepsSettingsCollapsibleLabel,
    getFormSessionStepsSettingsDescription,
    getFormSessionStepsSettingsSpeakers,
    getFormSessionStepsSettingsTags,
    getFormSessionStepsSettingsWaitlistLabel,
} from 'common/entities/form/sessionRegistration';
import {
    getFormInputTextStyles,
    getFormSessionDetailsTitleStyles,
    getFormSessionDetailsInfoStyles,
    getFormSessionDetailsBadgeStyles,
    getBadgeBorderStyles,
} from 'common/entities/form/styleConfiguration/selectors';
import { formatDateTime } from 'common/components/SessionCard/utils';
import transformValueToPixels from 'common/compounds/FormRenderer/utils/transformValueToPixels';
import { withFormId } from '../FormIdContext';
import FormSessionTags from './FormSessionTags';
import { convertDateFormat } from '../../utils/convertDateFormat';
import { toggleSelectedSession } from '../../actions/form';

const CHECKBOX_GAP = '1rem';
const CONTAINER_PADDING = '2px';

const Container = styled.div`
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
    color: ${({ fontColor }) => fontColor};
    letter-spacing: 0;
    padding-left: ${CONTAINER_PADDING};
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-left: ${({ checkboxSize }) =>
        `calc(${transformValueToPixels(checkboxSize)} + ${CHECKBOX_GAP} + ${CONTAINER_PADDING})`};
    opacity: ${({ isDisabled }) => (isDisabled ? 0.3 : 1)};
`;

const Header = styled.div`
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem;
`;

const Label = styled.label`
    display: flex;
    align-items: flex-start;
    gap: ${CHECKBOX_GAP};
`;

const Heading = styled.span`
    font-family: ${({ fontFamily }) => fontFamily};
    font-size: ${({ fontSize }) => (fontSize ? transformValueToPixels(fontSize) : 'inherit')};
    color: ${({ color }) => color};
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
    text-overflow: ellipsis;
    opacity: ${({ isDisabled }) => (isDisabled ? 0.3 : 1)};
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
`;

const DateText = styled.span`
    font-family: ${({ fontFamily }) => fontFamily};
    font-size: ${({ fontSize }) => transformValueToPixels(fontSize)};
    color: ${({ color }) => color};
    line-height: 1.15;
`;

const Text = styled.span`
    font-family: ${({ fontFamily }) => fontFamily};
    font-size: ${({ fontSize }) => transformValueToPixels(fontSize)};
    color: ${({ color }) => color};
    line-height: 1.5;
`;

const CollapsibleTrigger = styled(Collapsible.Trigger)`
    display: flex;
    background: transparent;
    margin: 0;
    padding: 0;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-family: ${({ fontFamily }) => fontFamily};
    font-size: ${({ fontSize }) => transformValueToPixels(fontSize)};
    color: ${({ color }) => color};
    line-height: 1.15;
    text-decoration: underline;

    &[data-disabled] {
        cursor: not-allowed;
    }
`;

const IconDown = styled(CaretDown)`
    transition: transform 300ms;

    ${CollapsibleTrigger}[data-state="open"] & {
        transform: rotate(180deg);
    }
`;

const CollapsibleContent = styled(Collapsible.Content)`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-top: 0.5rem;
`;

const Badge = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${({ borderRadius }) => transformValueToPixels(borderRadius)};
    border-color: ${({ borderColor }) => borderColor};
    border-style: solid;
    border-top-width: ${({ borderWidth }) => transformValueToPixels(borderWidth.sizes.top)};
    border-right-width: ${({ borderWidth }) => transformValueToPixels(borderWidth.sizes.right)};
    border-bottom-width: ${({ borderWidth }) => transformValueToPixels(borderWidth.sizes.bottom)};
    border-left-width: ${({ borderWidth }) => transformValueToPixels(borderWidth.sizes.left)};
    background-color: ${({ backgroundColor }) => backgroundColor};
    padding: 4px 8px;
    font-family: ${({ fontFamily }) => fontFamily};
    font-size: ${({ fontSize }) => transformValueToPixels(fontSize)};
    color: ${({ color }) => color};
    line-height: 1.41;
    white-space: nowrap;
    opacity: ${({ isDisabled }) => (isDisabled ? 0.3 : 1)};
`;

const Description = styled.p`
    font-family: ${({ fontFamily }) => fontFamily};
    font-size: ${({ fontSize }) => transformValueToPixels(fontSize)};
    color: ${({ color }) => color};
    line-height: 1.5;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 5;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const formatSpeakernfo = (speaker) => {
    if (!speaker) return '';

    const fullName = [speaker?.firstName, speaker?.lastName].filter(Boolean).join(' ');
    const titleAndCompany = [speaker?.jobTitle, speaker?.company].filter(Boolean).join(', ');

    return [fullName, titleAndCompany].filter(Boolean).join(', ');
};

const FormSessionListItem = ({
    formId,
    id,
    title,
    startTime,
    endTime,
    speakers,
    tags,
    description,
    waitlist,
    isExpanded,
    onToggleExpand,
    isSelected,
    isDisabled,
    status,
}) => {
    const dispatch = useDispatch();
    const collapsibleLabel = useSelector((state) => getFormSessionStepsSettingsCollapsibleLabel(state, formId));
    const waitlistLabel = useSelector((state) => getFormSessionStepsSettingsWaitlistLabel(state, formId));
    const speakersEnabled = useSelector((state) => getFormSessionStepsSettingsSpeakers(state, formId));
    const tagsEnabled = useSelector((state) => getFormSessionStepsSettingsTags(state, formId));
    const descriptionEnabled = useSelector((state) => getFormSessionStepsSettingsDescription(state, formId));
    const {
        colors: { answer },
        fontSize: checkboxSize,
    } = useSelector((state) => getFormInputTextStyles(state, formId));

    const {
        font: titleFont,
        fontSize: titleFontSize,
        color: titleColor,
    } = useSelector((state) => getFormSessionDetailsTitleStyles(state, formId));
    const {
        font: infoFont,
        fontSize: infoFontSize,
        color: infoColor,
    } = useSelector((state) => getFormSessionDetailsInfoStyles(state, formId));
    const {
        font: badgeFont,
        fontSize: badgeFontSize,
        color: badgeColor,
        background: badgeBackground,
    } = useSelector((state) => getFormSessionDetailsBadgeStyles(state, formId));
    const {
        color: borderColor,
        radius: borderRadius,
        ...borderWidth
    } = useSelector((state) => getBadgeBorderStyles(state, formId));

    const hideCollapsible = useMemo(() => {
        const areTagsAndDescriptionDisabled = !tagsEnabled && !descriptionEnabled;
        const isDescriptionAndTagsEmpty = isEmpty(description) && isEmpty(tags);
        const shouldHideForSpeakers = !speakersEnabled || speakers?.length <= 1;

        return (areTagsAndDescriptionDisabled || isDescriptionAndTagsEmpty) && shouldHideForSpeakers;
    }, [speakersEnabled, tagsEnabled, descriptionEnabled, speakers?.length, description, tags]);

    const handleCheckboxChange = () => {
        dispatch(toggleSelectedSession(id));
    };

    const renderBadge = cond([
        [
            () => waitlist && status === 'closed',
            () => (
                <Badge
                    fontFamily={badgeFont.value}
                    fontSize={badgeFontSize}
                    color={badgeColor.value}
                    backgroundColor={badgeBackground.value}
                    borderRadius={borderRadius}
                    borderWidth={borderWidth}
                    borderColor={borderColor.value}
                    isDisabled={isDisabled}
                >
                    {waitlistLabel}
                </Badge>
            ),
        ],
        [() => true, () => null],
    ]);

    return (
        <Container fontColor={answer?.value}>
            <Header>
                <Label data-qa={`SessionCheckbox__input-${id}`}>
                    <SuiCheckbox checked={isSelected} onChange={handleCheckboxChange} disabled={isDisabled} />
                    <Heading
                        fontFamily={titleFont.value}
                        fontSize={titleFontSize}
                        color={titleColor.value}
                        isDisabled={isDisabled}
                    >
                        {title}
                    </Heading>
                </Label>
                {renderBadge()}
            </Header>
            <ContentWrapper checkboxSize={checkboxSize} isDisabled={isDisabled}>
                <DateText fontFamily={infoFont.value} fontSize={infoFontSize} color={infoColor.value}>
                    <VisuallyHidden.Root>Session date and time: </VisuallyHidden.Root>
                    {formatDateTime(convertDateFormat(startTime), convertDateFormat(endTime))}
                </DateText>

                {speakersEnabled && speakers?.length > 0 ? (
                    <Text fontFamily={infoFont.value} fontSize={infoFontSize} color={infoColor.value}>
                        {formatSpeakernfo(speakers[0])}
                    </Text>
                ) : null}

                {!hideCollapsible ? (
                    <Collapsible.Root open={isExpanded} onOpenChange={onToggleExpand} disabled={isDisabled}>
                        <CollapsibleTrigger fontFamily={infoFont.value} fontSize={infoFontSize} color={infoColor.value}>
                            {collapsibleLabel}
                            <IconDown />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            {speakersEnabled && speakers?.length > 1 ? (
                                <ul aria-label="Additional speakers">
                                    {speakers.slice(1)?.map((speaker) => (
                                        <li key={speaker.id}>
                                            <Text
                                                fontFamily={infoFont.value}
                                                fontSize={infoFontSize}
                                                color={infoColor.value}
                                            >
                                                {formatSpeakernfo(speaker)}
                                            </Text>
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                            {tagsEnabled ? <FormSessionTags tags={tags} /> : null}
                            {descriptionEnabled && !!description ? (
                                <Description
                                    fontFamily={infoFont.value}
                                    fontSize={infoFontSize}
                                    color={infoColor.value}
                                >
                                    {description}
                                </Description>
                            ) : null}
                        </CollapsibleContent>
                    </Collapsible.Root>
                ) : null}
            </ContentWrapper>
        </Container>
    );
};

FormSessionListItem.propTypes = {
    formId: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    startTime: PropTypes.string.isRequired,
    endTime: PropTypes.string.isRequired,
    speakers: PropTypes.arrayOf(PropTypes.object).isRequired,
    tags: PropTypes.arrayOf(PropTypes.string).isRequired,
    description: PropTypes.string.isRequired,
    waitlist: PropTypes.bool,
    isExpanded: PropTypes.number,
    onToggleExpand: PropTypes.func,
    isSelected: PropTypes.bool.isRequired,
    isDisabled: PropTypes.bool,
    status: PropTypes.oneOf(['open', 'closed']).isRequired,
};

FormSessionListItem.defaultProps = {
    waitlist: false,
    isExpanded: null,
    onToggleExpand: noop,
    isDisabled: false,
};

export default withFormId(FormSessionListItem);
